import React, { useState } from "react";

// Merchant Subcomponents
import MerchantType from "./MerchantType/MerchantType";
import Banks from "./MerchantType/Banks";
import MerchantStatus from "./MerchantStatus/MerchantStatus";

// Ticket Subcomponents
import TicketType from "./TicketType/TicketType";
import TicketAgeCategories from "./TicketAgeCategory/TicketAgeCategory";
import TicketOrigin from "./TicketOrigin/TicketOrigin";
import TicketGroup from "./TicketGroup/TicketGroup";

// Address Subcomponents
import Province from "./Province/Province";
import District from "./District/District";
import CityDetails from "./CityDetails/CityDetails";

// Destination Subcomponents
import DestinationTypes from "./Destination/DestinationType.jsx";
import DestinationDetail from "./DestinationDetail/DestinationDetail";  // New component import

// User Subcomponents
import UserRoleDetails from "./Users/UserRoleDetails";

// General Subcomponents (NEW)
import CurrencyType from "./CurrencyType/CurrencyType";
import DiscountMode from "./DiscountMode/DiscountMode";

// Shared tab styling helpers (visual only - no behavior change)
const mainTabClass = (active) =>
  `px-5 h-9 rounded-md text-sm font-semibold transition-colors ${
    active ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-slate-100"
  }`;

const subTabClass = (active) =>
  `px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
    active
      ? "bg-brand-blue/10 text-brand-blue border-brand-blue/30"
      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
  }`;

const Destinations = ({ activeDestinationTab, onDestinationTabClick }) => (
  <div>
    <div className="flex flex-wrap gap-2 mb-6">
      <button
        className={subTabClass(activeDestinationTab === "destinationTypes")}
        onClick={() => onDestinationTabClick("destinationTypes")}
      >
        Destination Types
      </button>
      <button
        className={subTabClass(activeDestinationTab === "destinationDetails")}
        onClick={() => onDestinationTabClick("destinationDetails")}
      >
        Destination Detail
      </button>
    </div>
    <div>
      {activeDestinationTab === "destinationTypes" && <DestinationTypes />}
      {activeDestinationTab === "destinationDetails" && <DestinationDetail />}
    </div>
  </div>
);

const AddressSubTabs = ({ activeAddressTab, setActiveAddressTab }) => (
  <div>
    <div className="flex flex-wrap gap-2 mb-6">
      <button
        className={subTabClass(activeAddressTab === "province")}
        onClick={() => setActiveAddressTab("province")}
      >
        Province
      </button>
      <button
        className={subTabClass(activeAddressTab === "district")}
        onClick={() => setActiveAddressTab("district")}
      >
        District
      </button>
      <button
        className={subTabClass(activeAddressTab === "city")}
        onClick={() => setActiveAddressTab("city")}
      >
        City
      </button>
    </div>
    <div>
      {activeAddressTab === "province" && <Province />}
      {activeAddressTab === "district" && <District />}
      {activeAddressTab === "city" && <CityDetails />}
    </div>
  </div>
);

const MetaDashboardTabs = () => {
  const [activeTab, setActiveTab] = useState("merchants");

  // Sub-tabs for different main tabs
  const [activeMerchantTab, setActiveMerchantTab] = useState("merchantTypes");
  const [activeDestinationTab, setActiveDestinationTab] = useState("destinationTypes");
  const [activeTicketTab, setActiveTicketTab] = useState("ticketTypes");
  // Remove activeAddressTab from main tabs, move to General tab sub-tabs
  const [activeUserTab, setActiveUserTab] = useState("userRoles");

  // New General tab sub-tabs including Address as a sub-sub-tab group
  const [activeGeneralTab, setActiveGeneralTab] = useState("currencyType");

  // For address sub-tabs under General tab
  const [activeAddressTab, setActiveAddressTab] = useState("province");

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Meta Data Handler</h1>
        <div className="mt-1 text-sm text-slate-500">
          Home <span className="mx-1.5 text-slate-400">/</span>
          <span className="font-semibold text-slate-700">Settings</span>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="inline-flex flex-wrap gap-1 p-1 bg-white border rounded-lg border-slate-200">
        <button className={mainTabClass(activeTab === "merchants")} onClick={() => setActiveTab("merchants")}>
          Merchants
        </button>
        <button className={mainTabClass(activeTab === "destinations")} onClick={() => setActiveTab("destinations")}>
          Destinations
        </button>
        <button className={mainTabClass(activeTab === "tickets")} onClick={() => setActiveTab("tickets")}>
          Tickets
        </button>
        <button className={mainTabClass(activeTab === "users")} onClick={() => setActiveTab("users")}>
          Users
        </button>
        <button className={mainTabClass(activeTab === "general")} onClick={() => setActiveTab("general")}>
          General
        </button>
      </div>

      {/* Content Card */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        {/* Merchants Tab */}
        {activeTab === "merchants" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                className={subTabClass(activeMerchantTab === "merchantTypes")}
                onClick={() => setActiveMerchantTab("merchantTypes")}
              >
                Merchant Types
              </button>
              <button
                className={subTabClass(activeMerchantTab === "banks")}
                onClick={() => setActiveMerchantTab("banks")}
              >
                Banks &amp; Branches
              </button>
              <button
                className={subTabClass(activeMerchantTab === "merchantStatus")}
                onClick={() => setActiveMerchantTab("merchantStatus")}
              >
                Merchant Status
              </button>
            </div>
            <div>
              {activeMerchantTab === "merchantTypes" && <MerchantType />}
              {activeMerchantTab === "banks" && <Banks />}
              {activeMerchantTab === "merchantStatus" && <MerchantStatus />}
            </div>
          </div>
        )}

        {/* Destinations Tab */}
        {activeTab === "destinations" && (
          <Destinations activeDestinationTab={activeDestinationTab} onDestinationTabClick={setActiveDestinationTab} />
        )}

        {/* Tickets Tab */}
        {activeTab === "tickets" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                className={subTabClass(activeTicketTab === "ticketTypes")}
                onClick={() => setActiveTicketTab("ticketTypes")}
              >
                Ticket Types
              </button>
              <button
                className={subTabClass(activeTicketTab === "ticketAgeCategories")}
                onClick={() => setActiveTicketTab("ticketAgeCategories")}
              >
                Ticket Age Category
              </button>
              <button
                className={subTabClass(activeTicketTab === "ticketOrigin")}
                onClick={() => setActiveTicketTab("ticketOrigin")}
              >
                Ticket Origin
              </button>
              <button
                className={subTabClass(activeTicketTab === "ticketGroup")}
                onClick={() => setActiveTicketTab("ticketGroup")}
              >
                Ticket Group
              </button>
            </div>
            <div>
              {activeTicketTab === "ticketTypes" && <TicketType />}
              {activeTicketTab === "ticketAgeCategories" && <TicketAgeCategories />}
              {activeTicketTab === "ticketOrigin" && <TicketOrigin />}
              {activeTicketTab === "ticketGroup" && <TicketGroup />}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                className={subTabClass(activeUserTab === "userRoles")}
                onClick={() => setActiveUserTab("userRoles")}
              >
                User Roles
              </button>
            </div>
            <div>
              {activeUserTab === "userRoles" && <UserRoleDetails />}
            </div>
          </div>
        )}

        {/* General Tab */}
        {activeTab === "general" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                className={subTabClass(activeGeneralTab === "currencyType")}
                onClick={() => setActiveGeneralTab("currencyType")}
              >
                Currency Type
              </button>
              <button
                className={subTabClass(activeGeneralTab === "discountMode")}
                onClick={() => setActiveGeneralTab("discountMode")}
              >
                Discount Mode
              </button>
              <button
                className={subTabClass(activeGeneralTab === "address")}
                onClick={() => setActiveGeneralTab("address")}
              >
                Address
              </button>
            </div>
            <div>
              {activeGeneralTab === "currencyType" && <CurrencyType />}
              {activeGeneralTab === "discountMode" && <DiscountMode />}
              {activeGeneralTab === "address" && (
                <AddressSubTabs
                  activeAddressTab={activeAddressTab}
                  setActiveAddressTab={setActiveAddressTab}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetaDashboardTabs;
